<script setup>
import {onMounted, ref, watch} from 'vue'
import {codePreview, formatDate, showSuccessMessage} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import Textarea from '@/components/forms/Textarea.vue';
import Select from '@/components/forms/Select.vue';
import ClientService from "@/services/ClientService";
import ModalDialog from "@/components/modals/ModalDialog.vue";
import {createConfirmDialog} from "vuejs-confirm-dialog";


import {useI18n} from 'vue-i18n'

const {t} = useI18n()

const props = defineProps({
  clientId: {type: Number, required: true},
  investment: {type: Object, required: true},
});


const availableContracts = ref([]);
const isLoading = ref(false);
const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);

const changeContractId = ref(false);
const changedContractId = ref(props.investment.contract_id);

const investment = ref({...props.investment});

const closeModalWith = (value) => {
  isOpen.value = false;
  setTimeout(() => {
    if (value === 'confirm') {
      let object = {...investment.value};
      if(changeContractId.value) {
        object.change_contract_id = changeContractId.value;
        object.new_contract_id = changedContractId.value;
      }
      emit('confirm', object);
    } else {
      emit('cancel');
    }
  }, 300);
}

onMounted(() => {
  getAvailableContracts();
})

function openModal() {
  isOpen.value = true
}

async function getAvailableContracts() {
  await ClientService.getClientContractsv2(props.clientId)
    .then((response) => {
      availableContracts.value = response;
    })
    .catch((error) => {
      console.log(error);
    });
}

function deleteClientInvestment() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('updateClientInvestmentsModal.delete_strategy'),
    message: t('updateClientInvestmentsModal.confirm'),
    actionType: 'DELETE',
  });
  onConfirm(() => {
    ClientService.deleteInvestment(props.clientId, investment.value.id).then(() => {
      showSuccessMessage(t('updateClientInvestmentsModal.strategy_deleted'));
      emitter.emit('investmentDeleted', investment.value.id);
      closeModalWith('cancel');
    });
  });
  reveal();
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div"  class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">
                {{ t('updateClientInvestmentsModal.update_investment') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                  <div class="row" v-if="investment.meta.is_trashed">
                  <div class="col-12">
                    <div class="alert alert-danger">Tato strategie je smazána.</div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 mb-3 form-group">
                    <label for="strategy_name" class="form-label">{{ t('updateClientInvestmentsModal.strategy_name') }}</label>
                    <input v-model="investment.strategy.name" class="form-control" id="strategy_name" disabled />
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 mb-3 form-group">
                    <label for="contract_name" class="form-label">Smlouva</label>
                    <input :value="investment.contract ? investment.contract.contractType + ' (' + investment.contract.contractNumber + ')' : ''" class="form-control" id="strategy_name" disabled />
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 mb-2 form-group">
                    <div class="form-check mt-3 mb-3">
                      <input v-model="changeContractId" id="changeContractId" type="checkbox" class="form-check-input"/>
                      <label for="changeContractId" class="form-check-label">Změnit smlouvu</label>
                    </div>
                  </div>
                </div>
                <div v-if="changeContractId" class="row">
                  <div class="col-12 mb-3 form-group">
                    <label for="changedContractId" class="form-label">Nová smlouva</label>
                    <div v-if="isLoading" class="text-muted">{{ t('createClientInvestmentsModal.loading_data') }}</div>
                    <select v-if="investment" v-model="changedContractId" class="form-select" id="strategy">
                      <option :key="index" v-for="(contract, index) in availableContracts" :value="contract.id">{{ contract.contract_type?.name }} ({{ contract.contract_number}})</option>
                    </select>
                  </div>
                </div>

                <div class="row">
                  <div class="col-12 mb-3 form-group">
                    <label for="variable_symbol" class="form-label">{{ t('updateClientInvestmentsModal.variable_symbol') }}</label>
                    <input :disabled="investment.meta.is_trashed" v-model="investment.variable_symbol" class="form-control" id="variable_symbol" required/>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('updateClientInvestmentsModal.cancel') }}</button>
                <button v-if="!investment.meta.is_trashed" type="button" class="btn btn-danger" @click.prevent="deleteClientInvestment">{{ t('updateClientInvestmentsModal.delete') }}</button>
                <button v-if="!investment.meta.is_trashed" class="btn btn-primary btn-md" type="submit">{{ t('updateClientInvestmentsModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

